import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguage } from "@/contexts/LanguageContext";
import { Trash2, Plus } from "lucide-react";

interface Product {
  name: string;
  quantity: string;
  unit: string;
  price: string;
}

interface ProductListEditorProps {
  products: Product[];
  onProductsChange: (products: Product[]) => void;
  disabled?: boolean;
  /** Ref to first product name input for focus management */
  firstInputRef?: React.RefObject<HTMLInputElement>;
}

const UNIT_OPTIONS = [
  { value: "ცალი", labelKey: "units.piece" },
  { value: "კგ", labelKey: "units.kg" },
  { value: "ლიტრი", labelKey: "units.liter" },
  { value: "მ", labelKey: "units.meter" },
  { value: "კომპლექტი", labelKey: "units.set" },
] as const;

export const ProductListEditor = ({ products, onProductsChange, disabled = false, firstInputRef }: ProductListEditorProps) => {
  const { t } = useLanguage();

  const addProduct = () => {
    onProductsChange([...products, { name: "", quantity: "", unit: "ცალი", price: "" }]);
  };

  const updateProduct = (index: number, field: keyof Product, value: string) => {
    const updated = [...products];
    updated[index] = { ...updated[index], [field]: value };
    onProductsChange(updated);
  };

  const removeProduct = (index: number) => {
    onProductsChange(products.filter((_, i) => i !== index));
  };

  const validateQuantity = (value: string): boolean => {
    return value === "" || (!isNaN(Number(value)) && Number(value) > 0);
  };

  const validatePrice = (value: string): boolean => {
    return value === "" || (!isNaN(Number(value)) && Number(value) >= 0);
  };

  const handleQuantityChange = (index: number, value: string) => {
    if (validateQuantity(value)) {
      updateProduct(index, "quantity", value);
    }
  };

  const handlePriceChange = (index: number, value: string) => {
    if (validatePrice(value)) {
      updateProduct(index, "price", value);
    }
  };

  return (
    <div className="space-y-2">
      {/* Add button - only show when products already exist */}
      {products.length > 0 && (
        <div className="flex justify-end">
          <Button 
            type="button" 
            variant="ghost" 
            size="sm" 
            onClick={addProduct} 
            disabled={disabled}
            className="h-7 text-xs text-muted-foreground hover:text-foreground"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />{t("products.addProduct")}
          </Button>
        </div>
      )}

      {products.length === 0 ? (
        // Per spec: No apologetic empty states - show inline add row instead
        <button
          type="button"
          onClick={addProduct}
          disabled={disabled}
          className="w-full py-3 text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1.5 border border-dashed border-border rounded-md hover:border-primary/30"
        >
          <Plus className="h-4 w-4" />
          {t("products.addProduct")}
        </button>
      ) : (
        <div className="divide-y divide-border/30">
          {products.map((product, idx) => (
            <div key={idx} className="py-1.5 first:pt-0 last:pb-0">
              {/* Product name - full width */}
              <Input
                ref={idx === 0 ? firstInputRef : undefined}
                placeholder={t("products.productName")}
                value={product.name}
                onChange={(e) => updateProduct(idx, "name", e.target.value)}
                disabled={disabled}
                aria-label={`${t("products.productName")} ${idx + 1}`}
                className="mb-1.5"
              />
              
              {/* Row 2: Qty (small) + Unit (small) + Price (flex) + Delete */}
              <div className="grid grid-cols-[64px_60px_1fr_40px] gap-1.5">
                <Input
                  placeholder={t("products.quantity")}
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={product.quantity}
                  onChange={(e) => handleQuantityChange(idx, e.target.value)}
                  disabled={disabled}
                  aria-label={`${t("products.quantity")} ${idx + 1}`}
                  className="text-center"
                />
                <Select
                  value={product.unit}
                  onValueChange={(value) => updateProduct(idx, "unit", value)}
                  disabled={disabled}
                >
                  <SelectTrigger aria-label={`${t("units.piece")} ${idx + 1}`} className="h-9 text-xs px-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNIT_OPTIONS.map((unit) => (
                      <SelectItem key={unit.value} value={unit.value}>
                        {t(unit.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder={t("products.price")}
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={product.price}
                  onChange={(e) => handlePriceChange(idx, e.target.value)}
                  disabled={disabled}
                  aria-label={`${t("products.price")} ${idx + 1}`}
                  className="text-right"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeProduct(idx)}
                  disabled={disabled}
                  className="h-9 w-10 text-muted-foreground hover:text-destructive"
                  aria-label={`${t("products.remove")} ${idx + 1}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
